/**
 * Operation Steps Timeline Component
 * 
 * Displays the hierarchical step-by-step progress of an operation with:
 * - Real-time status updates
 * - Control plane vs data plane state visualization
 * - Dependency blocking visualization
 * - Progress tracking for multi-item steps (e.g., multiple ENIs)
 * - State mismatch warnings
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Server,
  Network,
  Shield,
  Database,
  Zap,
} from 'lucide-react';
import {
  StepWithDetails,
  StepStatus,
  StepType,
  PlaneState,
  StepProgress,
  StepBlockingFactor,
} from '../lib/api';
import { format } from 'date-fns';
import clsx from 'clsx';

// ============================================
// Step Type Icon Mapping
// ============================================
function getStepTypeIcon(type: StepType) {
  const iconMap: Partial<Record<StepType, React.ComponentType<any>>> = {
    ec2_terminate_instance: Server,
    ec2_wait_instance_terminated: Clock,
    ec2_wait_enis_released: Network,
    ec2_delete_eni: Network,
    ec2_detach_eni: Network,
    ec2_delete_security_group: Shield,
    ec2_revoke_sg_ingress: Shield,
    ec2_revoke_sg_egress: Shield,
    ec2_delete_nat_gateway: Network,
    ec2_wait_nat_deleted: Clock,
    ec2_delete_vpc_endpoints: Network,
    ec2_release_elastic_ip: Network,
    ec2_delete_subnet: Network,
    ec2_delete_route_table: Network,
    ec2_delete_igw: Network,
    ec2_delete_vpc: Network,
    ec2_verify_vpc_deleted: CheckCircle,
    ec2_delete_keypair: Shield,
    db_update_job_status: Database,
    network_teardown_orchestrate: Zap,
    cleanup_verification: CheckCircle,
  };
  return iconMap[type] || Server;
}

// ============================================
// Step Status Badge Component
// ============================================
function StepStatusBadge({ status }: { status: StepStatus }) {
  const config: Record<StepStatus, { 
    icon: React.ComponentType<any>; 
    color: string; 
    label: string; 
    animate?: boolean 
  }> = {
    queued: { 
      icon: Clock, 
      color: 'text-slate-400 bg-slate-500/10 border-slate-500/20', 
      label: 'Queued' 
    },
    in_progress: { 
      icon: Loader2, 
      color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', 
      label: 'In Progress', 
      animate: true 
    },
    waiting_on_dependency: { 
      icon: AlertCircle, 
      color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', 
      label: 'Waiting' 
    },
    retrying: { 
      icon: RefreshCw, 
      color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', 
      label: 'Retrying', 
      animate: true 
    },
    partially_complete: { 
      icon: AlertTriangle, 
      color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', 
      label: 'Partial' 
    },
    failed: { 
      icon: XCircle, 
      color: 'text-red-400 bg-red-500/10 border-red-500/20', 
      label: 'Failed' 
    },
    completed: { 
      icon: CheckCircle, 
      color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', 
      label: 'Completed' 
    },
    skipped: { 
      icon: Clock, 
      color: 'text-slate-400 bg-slate-500/10 border-slate-500/20', 
      label: 'Skipped' 
    },
    rolled_back: { 
      icon: RefreshCw, 
      color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', 
      label: 'Rolled Back' 
    },
  };

  const { icon: Icon, color, label, animate } = config[status];

  return (
    <span className={clsx(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
      color
    )}>
      <Icon className={clsx('w-3 h-3', animate && 'animate-spin')} />
      {label}
    </span>
  );
}

// ============================================
// Plane State Indicator Component
// ============================================
function PlaneStateIndicator({ 
  label, 
  state, 
  timestamp 
}: { 
  label: string; 
  state: PlaneState; 
  timestamp?: string;
}) {
  const config: Record<PlaneState, { color: string; icon: React.ComponentType<any>; text: string }> = {
    not_issued: { color: 'text-slate-500', icon: Clock, text: 'Pending' },
    issued: { color: 'text-blue-400', icon: Loader2, text: 'Issued' },
    confirmed: { color: 'text-emerald-400', icon: CheckCircle, text: 'Confirmed' },
    mismatch: { color: 'text-orange-400', icon: AlertTriangle, text: 'Mismatch' },
    failed: { color: 'text-red-400', icon: XCircle, text: 'Failed' },
  };

  const { color, icon: Icon, text } = config[state];

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-surface-500 w-14">{label}:</span>
      <Icon className={clsx('w-3.5 h-3.5', color, state === 'issued' && 'animate-spin')} />
      <span className={clsx('text-xs font-medium', color)}>{text}</span>
      {timestamp && (
        <span className="text-xs text-surface-600">
          ({format(new Date(timestamp), 'HH:mm:ss')})
        </span>
      )}
    </div>
  );
}

// ============================================
// Step Progress Bar Component
// ============================================
function StepProgressBar({ progress }: { progress: StepProgress }) {
  const percent = progress.total > 0 
    ? Math.round((progress.completed / progress.total) * 100) 
    : 0;

  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-surface-400">
          {progress.completed}/{progress.total} items
        </span>
        <span className="font-medium text-blue-400">{percent}%</span>
      </div>
      <div className="h-1.5 bg-surface-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.3 }}
          className={clsx(
            'h-full rounded-full',
            progress.failed > 0 ? 'bg-orange-500' : 'bg-blue-500'
          )}
        />
      </div>
      
      {/* Individual items */}
      {progress.items && progress.items.length > 0 && (
        <div className="mt-2 max-h-32 overflow-y-auto">
          <div className="space-y-1">
            {progress.items.map((item) => (
              <div 
                key={item.id} 
                className="flex items-center gap-2 text-xs py-0.5 px-2 rounded bg-surface-900/50"
              >
                {item.status === 'completed' && (
                  <CheckCircle className="w-3 h-3 text-emerald-400" />
                )}
                {item.status === 'in_progress' && (
                  <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
                )}
                {item.status === 'failed' && (
                  <XCircle className="w-3 h-3 text-red-400" />
                )}
                {item.status === 'pending' && (
                  <Clock className="w-3 h-3 text-slate-500" />
                )}
                <span className={clsx(
                  'truncate flex-1',
                  item.status === 'completed' ? 'text-surface-400' : 'text-surface-300'
                )}>
                  {item.name}
                </span>
                {item.error && (
                  <span className="text-red-400 truncate max-w-[150px]" title={item.error}>
                    {item.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Blocking Factor Alert Component
// ============================================
function BlockingFactorAlert({ factors }: { factors: StepBlockingFactor[] }) {
  return (
    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
      <h5 className="text-xs font-medium text-amber-400 mb-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        Blocking Factors
      </h5>
      {factors.map((factor, i) => (
        <div key={i} className="text-xs text-amber-300/80 mb-1">
          <span className="font-medium capitalize">{factor.type.replace(/_/g, ' ')}:</span>{' '}
          {factor.description}
          {factor.awsErrorCode && (
            <code className="ml-1 px-1 bg-amber-500/20 rounded">{factor.awsErrorCode}</code>
          )}
          {factor.suggestedAction && (
            <div className="mt-0.5 text-amber-400/60">
              💡 {factor.suggestedAction}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================
// Single Step Row Component
// ============================================
interface StepRowProps {
  step: StepWithDetails;
  depth?: number;
  isLast?: boolean;
}

function StepRow({ step, depth = 0, isLast = false }: StepRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = step.childSteps && step.childSteps.length > 0;
  const Icon = getStepTypeIcon(step.type);

  const isActive = step.status === 'in_progress' || step.status === 'retrying';
  const showDetails = step.hasStateMismatch || 
    (step.blockingFactors && step.blockingFactors.length > 0) ||
    step.errorMessage ||
    step.progress;

  return (
    <div className="relative">
      {/* Connector line */}
      {depth > 0 && (
        <div 
          className={clsx(
            'absolute left-3 w-0.5 bg-surface-700',
            isLast ? 'top-0 h-4' : 'top-0 bottom-0'
          )}
          style={{ marginLeft: (depth - 1) * 24 }}
        />
      )}
      
      <div 
        className={clsx(
          'relative flex items-start gap-3 p-3 rounded-lg transition-all',
          isActive && 'bg-blue-500/5 border border-blue-500/20',
          step.hasStateMismatch && 'bg-orange-500/5 border border-orange-500/20',
          step.status === 'failed' && 'bg-red-500/5 border border-red-500/20',
          !isActive && !step.hasStateMismatch && step.status !== 'failed' && 'hover:bg-surface-800/50'
        )}
        style={{ marginLeft: depth * 24 }}
      >
        {/* Status Icon */}
        <div className="flex flex-col items-center pt-0.5">
          <div
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              step.status === 'completed' && 'bg-emerald-500/20',
              step.status === 'in_progress' && 'bg-blue-500/20',
              step.status === 'waiting_on_dependency' && 'bg-amber-500/20',
              step.status === 'retrying' && 'bg-orange-500/20',
              step.status === 'failed' && 'bg-red-500/20',
              step.status === 'queued' && 'bg-slate-500/20',
              step.status === 'partially_complete' && 'bg-yellow-500/20',
              step.status === 'skipped' && 'bg-slate-500/10'
            )}
          >
            {step.status === 'completed' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
            {step.status === 'in_progress' && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
            {step.status === 'waiting_on_dependency' && <AlertCircle className="w-4 h-4 text-amber-400" />}
            {step.status === 'retrying' && <RefreshCw className="w-4 h-4 text-orange-400 animate-spin" />}
            {step.status === 'failed' && <XCircle className="w-4 h-4 text-red-400" />}
            {step.status === 'queued' && <Clock className="w-4 h-4 text-slate-400" />}
            {step.status === 'partially_complete' && <AlertTriangle className="w-4 h-4 text-yellow-400" />}
            {step.status === 'skipped' && <span className="text-slate-500 text-xs">—</span>}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Expand button for steps with children or details */}
            {(hasChildren || showDetails) && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-surface-500 hover:text-surface-300"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            )}

            {/* Timestamp */}
            {step.startedAt && (
              <span className="text-xs text-surface-500 font-mono w-16">
                {format(new Date(step.startedAt), 'HH:mm:ss')}
              </span>
            )}

            {/* Step label */}
            <span className="text-sm font-medium text-surface-200">{step.label}</span>

            {/* Status badge */}
            <StepStatusBadge status={step.status} />

            {/* Type icon */}
            <Icon className="w-3.5 h-3.5 text-surface-500" />

            {/* Duration */}
            {step.durationMs != null && (
              <span className="text-xs text-surface-500">
                {step.durationMs < 1000
                  ? `${step.durationMs}ms`
                  : step.durationMs < 60000
                  ? `${(step.durationMs / 1000).toFixed(1)}s`
                  : `${Math.round(step.durationMs / 60000)}m`}
              </span>
            )}

            {/* Estimated duration for active steps */}
            {isActive && step.estimatedDurationMs && !step.durationMs && (
              <span className="text-xs text-surface-500">
                (~{Math.round(step.estimatedDurationMs / 1000)}s estimated)
              </span>
            )}

            {/* Retry count */}
            {step.retryCount > 0 && (
              <span className="text-xs text-orange-400">
                Retry {step.retryCount}/{step.maxRetries}
              </span>
            )}

            {/* State mismatch indicator */}
            {step.hasStateMismatch && (
              <span className="text-xs text-orange-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                State Mismatch
              </span>
            )}
          </div>

          {/* Target resource */}
          {step.targetResourceId && (
            <div className="mt-1 text-xs text-surface-400">
              {step.targetResourceType}:{' '}
              <code className="text-surface-300 bg-surface-800 px-1 rounded">
                {step.targetResourceId}
              </code>
            </div>
          )}

          {/* Expanded details */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-3 space-y-3">
                  {/* Control/Data plane state */}
                  <div className="p-2 bg-surface-800/50 rounded-lg">
                    <div className="flex flex-col gap-1">
                      <PlaneStateIndicator 
                        label="Control" 
                        state={step.controlPlaneState}
                        timestamp={step.controlPlaneIssuedAt}
                      />
                      <PlaneStateIndicator 
                        label="Data" 
                        state={step.dataPlaneState}
                        timestamp={step.dataPlaneConfirmedAt}
                      />
                      {step.expectedState && step.actualState && step.expectedState !== step.actualState && (
                        <div className="text-xs text-orange-400 mt-1">
                          Expected: <code className="bg-surface-800 px-1 rounded">{step.expectedState}</code>
                          {' → '}
                          Actual: <code className="bg-orange-500/20 px-1 rounded">{step.actualState}</code>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress bar for multi-item steps */}
                  {step.progress && <StepProgressBar progress={step.progress} />}

                  {/* Blocking factors */}
                  {step.blockingFactors && step.blockingFactors.length > 0 && (
                    <BlockingFactorAlert factors={step.blockingFactors} />
                  )}

                  {/* Error message */}
                  {step.errorMessage && (
                    <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <h5 className="text-xs font-medium text-red-400 mb-1">Error</h5>
                      <p className="text-xs text-red-300">
                        {step.errorCode && (
                          <code className="mr-1 px-1 bg-red-500/20 rounded">[{step.errorCode}]</code>
                        )}
                        {step.errorMessage}
                      </p>
                      {step.isRetryable && step.retryAvailable && (
                        <p className="text-xs text-red-400/60 mt-1">
                          This step can be retried ({step.maxRetries - step.retryCount} attempts remaining)
                        </p>
                      )}
                    </div>
                  )}

                  {/* AWS Request ID */}
                  {step.awsRequestId && (
                    <div className="text-xs text-surface-500">
                      AWS Request ID: <code className="text-surface-400">{step.awsRequestId}</code>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Active indicator pulse */}
        {isActive && (
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-blue-500"
          />
        )}
      </div>

      {/* Child steps */}
      {isExpanded && hasChildren && (
        <div className="mt-1">
          {step.childSteps!.map((child, idx) => (
            <StepRow 
              key={child.id} 
              step={child} 
              depth={depth + 1}
              isLast={idx === step.childSteps!.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Timeline Component
// ============================================
interface OperationStepsTimelineProps {
  steps: StepWithDetails[];
  stepStats?: {
    total: number;
    completed: number;
    failed: number;
    inProgress: number;
    blocked: number;
    pending: number;
  };
}

export default function OperationStepsTimeline({ steps, stepStats }: OperationStepsTimelineProps) {
  if (!steps || steps.length === 0) {
    return (
      <div className="text-center py-8 text-surface-500">
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No steps recorded for this operation</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats header */}
      {stepStats && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-surface-800/50 rounded-lg">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-surface-400">Steps:</span>
            <span className="text-sm font-medium text-white">{stepStats.total}</span>
          </div>
          <div className="h-4 w-px bg-surface-700" />
          <div className="flex items-center gap-3 text-xs">
            {stepStats.completed > 0 && (
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-3 h-3" /> {stepStats.completed}
              </span>
            )}
            {stepStats.inProgress > 0 && (
              <span className="flex items-center gap-1 text-blue-400">
                <Loader2 className="w-3 h-3 animate-spin" /> {stepStats.inProgress}
              </span>
            )}
            {stepStats.blocked > 0 && (
              <span className="flex items-center gap-1 text-amber-400">
                <AlertCircle className="w-3 h-3" /> {stepStats.blocked}
              </span>
            )}
            {stepStats.failed > 0 && (
              <span className="flex items-center gap-1 text-red-400">
                <XCircle className="w-3 h-3" /> {stepStats.failed}
              </span>
            )}
            {stepStats.pending > 0 && (
              <span className="flex items-center gap-1 text-slate-400">
                <Clock className="w-3 h-3" /> {stepStats.pending}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Steps timeline */}
      <div className="space-y-1">
        {steps.map((step, idx) => (
          <StepRow 
            key={step.id} 
            step={step}
            isLast={idx === steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
